import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTrigger } from 'shared/components/ui/dialog';
import { Input } from 'shared/components/ui/input';
import { FinecoButton } from '../../FinecoButton';
import colorsStore from 'shared/store/colorsStore';

import styles from './SaveListModal.module.scss';

type SaveListModalProps = {
  isins: string[];
  onSave: (data: { name: string; isins: string[] }) => Promise<void>;
  children: React.ReactNode;
};

export const SaveListModal: React.FC<SaveListModalProps> = ({ isins, onSave, children }) => {
  const { t } = useTranslation();
  // Subscribes to color-config changes so the component re-renders when they update.
  colorsStore((state) => state.colorsConfigStore);

  const [open, setOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inFlightRef = React.useRef(false);

  const handleSubmit = async () => {
    if (!listName.trim()) {
      return;
    }
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);

    try {
      await onSave({
        name: listName.trim(),
        isins,
      });
      setListName('');
      setOpen(false);
    } catch (error) {
      // Error handled by parent with toast.
    } finally {
      setSubmitting(false);
      inFlightRef.current = false;
    }
  };

  const handleCancel = () => {
    setListName('');
    setOpen(false);
  };

  const isSubmitDisabled = submitting || !listName.trim();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent title={t('instrumentLists.saveList')} className={styles.dialogContent}>
        <div className={styles.modalBody}>
          <div className={styles.nameInputContainer}>
            <Input
              id="listName"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={t('instrumentLists.enterListName')}
              disabled={submitting}
              autoFocus
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: '12px',
          borderTop: '1px solid #e5e7eb',
          padding: '16px 24px',
          marginLeft: '-24px',
          marginRight: '-24px',
          marginBottom: '-24px'
        }}>
          <FinecoButton
            click={handleCancel}
            variant="white"
            copy={t('actions.cancel')}
            disabled={submitting}
          />
          <FinecoButton
            click={handleSubmit}
            copy={submitting ? t('actions.submitting') : t('instrumentLists.saveList')}
            disabled={isSubmitDisabled}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
